// src/products/dataroom/components/Modals/AddPartyModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Building2, CheckCircle } from 'lucide-react';
import { supabase } from '../../../../config';
import partyTeamService from '../../services/partyTeamService';

const AddPartyModal = ({ isOpen, onClose, onComplete }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [parties, setParties] = useState([{ name: '', code: '', color: '#3B82F6', description: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const predefinedColors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280'
  ];

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select('id, project_name')
        .order('project_name');

      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const addPartyRow = () => {
    // Find next available color
    const usedColors = parties.map(p => p.color);
    const nextColor = predefinedColors.find(c => !usedColors.includes(c)) || predefinedColors[0];
    
    setParties([...parties, { name: '', code: '', color: nextColor, description: '' }]);
  };

  const removePartyRow = (index) => {
    if (parties.length > 1) {
      setParties(parties.filter((_, i) => i !== index));
    }
  };

  const updateParty = (index, field, value) => {
    const updated = [...parties];
    updated[index][field] = value;
    setParties(updated);
  };

  const handleSubmit = async () => {
    if (!selectedProject) {
      setError('Please select a dataroom project');
      return;
    }

    const validParties = parties.filter(p => p.name.trim() && p.code.trim());
    if (validParties.length === 0) {
      setError('Please add at least one party with name and code');
      return;
    }

    // Check for duplicate codes
    const codes = validParties.map(p => p.code.toUpperCase());
    const duplicateCodes = codes.filter((c, i) => codes.indexOf(c) !== i);
    if (duplicateCodes.length > 0) {
      setError(`Duplicate code(s): ${duplicateCodes.join(', ')}`);
      return;
    }

    // Check for duplicate colors
    const colors = validParties.map(p => p.color);
    const duplicateColors = colors.filter((c, i) => colors.indexOf(c) !== i);
    if (duplicateColors.length > 0) {
      setError('Each party must have a unique color');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const savedParties = [];

      for (const party of validParties) {
        const result = await partyTeamService.createParty(parseInt(selectedProject), {
          name: party.name.trim(),
          code: party.code.toUpperCase().trim(),
          color: party.color,
          description: party.description.trim() || 'Custom party',
          party_type: 'custom'
        });

        if (result.error) {
          console.error(`Failed to create party "${party.name}":`, result.error);
          continue;
        }

        console.log(`✓ Created party: ${party.name}`);
        savedParties.push(result.data);
      }

      if (savedParties.length > 0) {
        onComplete && onComplete(savedParties);
        handleClose();
      } else {
        setError('Failed to create parties');
      }
    } catch (err) {
      console.error('Error creating parties:', err);
      setError(err.message || 'Failed to create parties');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProject('');
    setParties([{ name: '', code: '', color: '#3B82F6', description: '' }]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Building2 className="w-6 h-6 text-white mr-3" />
            <h2 className="text-xl font-bold text-white">Add Parties</h2>
          </div>
          <button onClick={handleClose} className="text-white hover:text-purple-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Select Dataroom Project *
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select Project...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.project_name}
                </option>
              ))}
            </select>
          </div>

          {/* Parties Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Parties to Add
              </label>
              <button
                onClick={addPartyRow}
                className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Another Party
              </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {parties.map((party, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {/* Code */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Code (3 chars) *
                      </label>
                      <input
                        type="text"
                        placeholder="HTT"
                        value={party.code}
                        onChange={(e) => updateParty(index, 'code', e.target.value.toUpperCase())}
                        maxLength={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    {/* Name */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Party Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Investment Firm ABC"
                        value={party.name}
                        onChange={(e) => updateParty(index, 'name', e.target.value)}
                        maxLength={50}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    {/* Remove Button */}
                    <div className="flex items-end">
                      <button
                        onClick={() => removePartyRow(index)}
                        disabled={parties.length === 1}
                        className="w-full p-2 text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed border border-gray-300 rounded-md hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  </div>

                  {/* Color Picker */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Color *
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {predefinedColors.map((color) => {
                        const isUsed = parties.some((p, i) => i !== index && p.color === color);
                        const isSelected = party.color === color;
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => !isUsed && updateParty(index, 'color', color)}
                            disabled={isUsed}
                            className={`w-10 h-10 rounded-md border-2 transition-all ${
                              isSelected 
                                ? 'border-gray-900 scale-110 shadow-md' 
                                : isUsed
                                  ? 'border-gray-200 opacity-30 cursor-not-allowed'
                                  : 'border-gray-300 hover:border-gray-500 hover:scale-105'
                            }`}
                            style={{ backgroundColor: color }}
                            title={isUsed ? 'Already used' : isSelected ? 'Selected' : color}
                          >
                            {isSelected && (
                              <CheckCircle className="w-5 h-5 text-white mx-auto" strokeWidth={3} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Description (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Lead investor group"
                      value={party.description}
                      onChange={(e) => updateParty(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedProject}
            className="flex items-center px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Parties...
              </>
            ) : (
              <>
                <Building2 className="w-4 h-4 mr-2" />
                Create {parties.filter(p => p.name.trim() && p.code.trim()).length} Part{parties.filter(p => p.name.trim() && p.code.trim()).length === 1 ? 'y' : 'ies'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddPartyModal;









